import { useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Popover,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Pagination,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { alpha } from "@mui/material/styles";
import { formatWordPreview, stripLeadingPrefix } from "../../utils/helpers";

const QUERY_CONTENT_WORD_LIMIT = 6;
const QUERY_CONTENT_PREFIX = "text";

const STATUS_STYLES = {
  Incomplete: "warning",
  Complete: "success",
  Closed: "secondary",
};

const headerCellSx = {
  backgroundColor: (theme) => theme.palette.grey[100],
  fontWeight: "bold",
  textTransform: "uppercase",
  fontSize: "0.85rem",
  color: "#424242",
  whiteSpace: "nowrap",
};

const formatQueryContent = (raw) =>
  formatWordPreview(stripLeadingPrefix(raw, QUERY_CONTENT_PREFIX), QUERY_CONTENT_WORD_LIMIT);

const parseRecipients = (raw) => {
  if (raw == null || raw === "" || raw === "[]" || raw === "None") {
    return [];
  }

  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (typeof item === "string") {
          const inner = item.replace(/^\{|\}$/g, "");
          const [email, ...roleParts] = inner.split(",").map((part) => part.trim());
          return email ? { email, role: roleParts.join(", ") } : null;
        }
        if (item?.email) {
          return { email: item.email, role: item.role || "" };
        }
        return null;
      })
      .filter(Boolean);
  }

  const matches = [...String(raw).matchAll(/\{([^}]+)\}/g)];
  return matches
    .map((match) => {
      const [email, ...roleParts] = match[1].split(",").map((part) => part.trim());
      return email ? { email, role: roleParts.join(", ") } : null;
    })
    .filter(Boolean);
};

const getQueryStatuses = (log) => {
  const content = stripLeadingPrefix(log.query_content, QUERY_CONTENT_PREFIX);
  const isClosedEvent = /^query closed$/i.test(content);
  const tokens = String(log.query_status || "")
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean);

  const statuses = [];
  const seen = new Set();
  const add = (label) => {
    if (seen.has(label)) return;
    seen.add(label);
    statuses.push(label);
  };

  if (isClosedEvent) {
    add("Closed");
  }

  tokens.forEach((token) => {
    const normalized = token.replace(/\s/g, "").toLowerCase();
    if (normalized === "incomplete") {
      add("Incomplete");
    } else if (normalized === "complete") {
      if (!isClosedEvent) add("Complete");
    } else if (normalized === "closed") {
      add("Closed");
    }
  });

  return statuses;
};

const PersonCell = ({ email, role }) => {
  if (!email) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="body2">{email}</Typography>
      {role ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {role}
        </Typography>
      ) : null}
    </Box>
  );
};

const QueryStatusBadges = ({ log }) => {
  const statuses = getQueryStatuses(log);
  if (statuses.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {statuses.map((label) => {
        const color = STATUS_STYLES[label] || "default";
        return (
          <Chip
            key={label}
            size="small"
            color={color}
            label={label}
            sx={{
              fontWeight: 600,
              borderRadius: 1.5,
              ...(color !== "default" && {
                bgcolor: (theme) => alpha(theme.palette[color].main, 0.12),
                color: (theme) => theme.palette[color].dark || theme.palette[color].main,
              }),
            }}
          />
        );
      })}
    </Stack>
  );
};

const RecipientsCell = ({ raw }) => {
  const recipients = parseRecipients(raw);
  if (recipients.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {recipients.map((recipient) => (
        <Chip
          key={recipient.email}
          size="small"
          label={
            recipient.role ? `${recipient.email} (${recipient.role})` : recipient.email
          }
        />
      ))}
    </Stack>
  );
};

const QueryContentCell = ({ log, expanded, onToggle }) => {
  const { preview, truncated } = formatQueryContent(log.query_content);
  if (!preview) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
      <Typography
        variant="body2"
        onClick={(event) => onToggle(event, log)}
        sx={{
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {preview}
      </Typography>
      {truncated ? (
        <Tooltip title="View more">
          <IconButton
            size="small"
            aria-label="View more"
            onClick={(event) => onToggle(event, log)}
            sx={{ p: 0.25 }}
          >
            <KeyboardArrowRightIcon
              fontSize="small"
              sx={(theme) => ({
                transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                transition: theme.transitions.create("transform", {
                  duration: theme.transitions.duration.shorter,
                }),
              })}
            />
          </IconButton>
        </Tooltip>
      ) : null}
    </Stack>
  );
};

const QueryChangeLogTable = ({ queryLogs, handlePageClick, pageItemCount, currentPage = 0 }) => {
  const [detailLog, setDetailLog] = useState(null);
  const [detailAnchor, setDetailAnchor] = useState(null);
  const detailContent = formatQueryContent(detailLog?.query_content).full;

  const closeDetails = () => {
    setDetailLog(null);
    setDetailAnchor(null);
  };

  const toggleDetails = (event, log) => {
    event.stopPropagation();
    const isSameRow = detailLog && (detailLog.id ?? detailLog) === (log.id ?? log);
    if (isSameRow) {
      closeDetails();
      return;
    }
    setDetailLog(log);
    setDetailAnchor(event.currentTarget);
  };

  return (
    <Box sx={{ py: 2 }}>
      <TableContainer component={Paper} sx={{ maxHeight: "80vh" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>SiteID – SubjectsID</TableCell>
              <TableCell sx={headerCellSx}>Trial Name</TableCell>
              <TableCell sx={headerCellSx}>Visit Name</TableCell>
              <TableCell sx={headerCellSx}>Visit Type</TableCell>
              <TableCell sx={headerCellSx}>Block Name</TableCell>
              <TableCell sx={headerCellSx}>Entered By</TableCell>
              <TableCell sx={headerCellSx}>Query Sent by</TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 168 }}>Timestamp</TableCell>
              <TableCell sx={headerCellSx}>Action</TableCell>
              <TableCell sx={headerCellSx}>Query Content</TableCell>
              <TableCell sx={headerCellSx}>Query Status</TableCell>
              <TableCell sx={headerCellSx}>Recipients</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {queryLogs?.results?.length > 0 ? (
              queryLogs.results.map((log, index) => (
                <TableRow key={log.id ?? index} hover>
                  <TableCell>{log.site_participant_id}</TableCell>
                  <TableCell>{log.trial_name}</TableCell>
                  <TableCell>{log.visit_name}</TableCell>
                  <TableCell>{log.visit_type || "—"}</TableCell>
                  <TableCell>{log.block_name}</TableCell>
                  <TableCell>
                    <PersonCell email={log.block_filled_by_email} role={log.block_filled_by_role} />
                  </TableCell>
                  <TableCell>
                    <PersonCell email={log.query_sender_email} role={log.query_sender_role} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 168, whiteSpace: "nowrap" }}>
                    {new Date(log.created_at + "Z").toDateString()}
                    <br />
                    {new Date(log.created_at + "Z").toLocaleTimeString()}
                  </TableCell>
                  <TableCell>{log.action}</TableCell>
                  <TableCell>
                    <QueryContentCell
                      log={log}
                      expanded={Boolean(detailLog) && (detailLog.id ?? index) === (log.id ?? index)}
                      onToggle={toggleDetails}
                    />
                  </TableCell>
                  <TableCell>
                    <QueryStatusBadges log={log} />
                  </TableCell>
                  <TableCell>
                    <RecipientsCell raw={log.recipients} />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No query change logs available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="center" sx={{ pt: 2 }}>
        <Pagination
          color="primary"
          page={currentPage + 1}
          count={Math.ceil(queryLogs?.count / pageItemCount) || 0}
          onChange={(_, page) => handlePageClick({ selected: page - 1 })}
        />
      </Stack>

      <Popover
        open={Boolean(detailLog)}
        anchorEl={detailAnchor}
        onClose={closeDetails}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 2, maxWidth: 420 }}>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {detailContent || "—"}
          </Typography>
        </Box>
      </Popover>
    </Box>
  );
};

export default QueryChangeLogTable;
